import { Directive, ElementRef, OnDestroy, OnInit } from '@angular/core';

/**
 * Directive to fix PrimeNG InputOTP issues:
 * 1. event.code undefined error causing "undefined is not an object (evaluating 'e.code.startsWith')"
 * 2. Autofill putting all OTP digits into first input on Safari 18 and some Android devices
 * 
 * This directive intercepts events and properly distributes autofilled digits across all inputs.
 */
@Directive({
  // eslint-disable-next-line @angular-eslint/directive-selector
  selector: 'p-inputOtp',
  standalone: true
})
export class InputOtpFixDirective implements OnInit, OnDestroy {
  private inputs: HTMLInputElement[] = [];
  private observer?: MutationObserver;

  constructor(private el: ElementRef) {
    this.el.nativeElement.addEventListener('keydown', (event: KeyboardEvent) => {
      // Fix for undefined event.code that causes crashes in PrimeNG
      if (!event.code) {
        Object.defineProperty(event, 'code', {
          value: '',
          writable: false,
          configurable: true
        });
      }
    }, true);
  }

  ngOnInit(): void {
    const initializeInputs = () => {
      const nextInputs = Array.from(this.el.nativeElement.querySelectorAll('input'));
      if (nextInputs.length === 0) {
        return;
      }

      this.inputs = nextInputs as HTMLInputElement[];
      this.inputs.forEach((input) => this.configureInput(input));
      this.setupAutofillHandlers();
    };

    setTimeout(initializeInputs);

    this.observer = new MutationObserver(() => {
      setTimeout(initializeInputs);
    });
    this.observer.observe(this.el.nativeElement, { childList: true, subtree: true, attributes: true });
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
  }

  private configureInput(input: HTMLInputElement): void {
    if (input.dataset.otpInputPatched === 'true') {
      return;
    }

    input.type = 'text';
    input.setAttribute('inputmode', 'numeric');
    input.setAttribute('pattern', '[0-9]*');
    input.setAttribute('maxlength', '1');
    input.setAttribute('autocomplete', 'one-time-code');
    input.setAttribute('autocapitalize', 'none');
    input.setAttribute('autocorrect', 'off');
    input.setAttribute('spellcheck', 'false');
    input.setAttribute('enterkeyhint', 'done');
    input.setAttribute('aria-label', 'One-time password');
    input.setAttribute('data-otp-input', 'true');
    input.style.webkitAppearance = 'none';
    input.style.fontSize = '16px';
    input.style.fontFamily = 'Arial, sans-serif';

    input.addEventListener('keydown', this.handleKeyDown);
    input.addEventListener('beforeinput', this.handleBeforeInput);
    input.addEventListener('input', this.handleInput);
    input.addEventListener('keyup', this.handleInput);
  }

  private handleKeyDown = (event: KeyboardEvent): void => {
    const key = event.key ?? '';
    const code = event.code ?? '';
    const allowedKeys = ['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Enter', 'Escape', 'Home', 'End'];
    const normalizedCode = code.replace('Digit', '').replace('Numpad', '');
    const isDigit = /^\d$/.test(key) || /^\d$/.test(normalizedCode) || /^[0-9]$/.test((event as any).data ?? '') && (key === 'Unidentified' || key === '');
    const isNavigationKey = allowedKeys.includes(key) || allowedKeys.includes(code);

    if (!isDigit && !isNavigationKey && !event.ctrlKey && !event.metaKey && !event.altKey) {
      event.preventDefault();
    }
  };

  private handleBeforeInput = (event: InputEvent): void => {
    if (!event.data) {
      return;
    }

    const isDigit = /^\d$/.test(event.data);
    if (!isDigit && event.inputType !== 'deleteContentBackward') {
      event.preventDefault();
    }
  };

  private handleInput = (event: Event): void => {
    const input = event.target as HTMLInputElement;
    const digits = input.value.replace(/\D/g, '').slice(0, 1);
    if (input.value !== digits) {
      input.value = digits;
      input.dispatchEvent(new Event('input', { bubbles: true }));
    }
    input.type = 'text';
    input.setAttribute('inputmode', 'numeric');
    input.setAttribute('pattern', '[0-9]*');
    input.setAttribute('maxlength', '1');
  };

  private setupAutofillHandlers(): void {
    this.inputs.forEach((input) => {
      // Handle input event for autofill detection
      input.addEventListener('input', (event: Event) => {
        const target = event.target as HTMLInputElement;
        const value = target.value;

        // Check if multiple digits were pasted/autofilled into one input
        if (value && value.length > 1) {
          event.preventDefault();
          event.stopPropagation();
          
          // Distribute the digits across all inputs
          this.distributeDigits(value);
        }
      });

      // Handle paste event
      input.addEventListener('paste', (event: ClipboardEvent) => {
        const pastedData = event.clipboardData?.getData('text');
        
        if (pastedData && pastedData.length > 1) {
          event.preventDefault();
          event.stopPropagation();
          
          // Distribute the pasted digits
          this.distributeDigits(pastedData);
        }
      });

      // Handle autofill with animationstart event (webkit browsers)
      input.addEventListener('animationstart', (event: AnimationEvent) => {
        if (event.animationName === 'onAutoFillStart') {
          setTimeout(() => {
            const value = (event.target as HTMLInputElement).value;
            if (value && value.length > 1) {
              this.distributeDigits(value);
            }
          }, 0);
        }
      });
    });
  }

  private distributeDigits(value: string): void {
    // Clean the value to only include digits
    const digits = value.replace(/\D/g, '');
    
    // Clear all inputs first
    this.inputs.forEach(input => {
      input.value = '';
      // Trigger input event to update the component's internal state
      input.dispatchEvent(new Event('input', { bubbles: true }));
    });

    // Distribute digits starting from the first input
    const digitsToDistribute = digits.slice(0, this.inputs.length);
    digitsToDistribute.split('').forEach((digit, idx) => {
      if (this.inputs[idx]) {
        this.inputs[idx].value = digit;
        // Trigger input event for each digit
        this.inputs[idx].dispatchEvent(new Event('input', { bubbles: true }));
      }
    });

    // Focus the next empty input or the last input
    const nextEmptyIndex = this.inputs.findIndex(input => !input.value);
    const focusIndex = nextEmptyIndex >= 0 ? nextEmptyIndex : this.inputs.length - 1;
    this.inputs[focusIndex]?.focus();
  }
}
